// Seed blog posts for SEO. Content uses simple structured blocks for safe rendering.
// Add more posts here as the business grows.

export const POSTS = [
  {
    slug: "how-often-reseal-parking-lot-memphis",
    title: "How often should you reseal a parking lot in Memphis?",
    description:
      "Memphis humidity, freeze-thaw cycles, and summer heat all attack asphalt. Here's the schedule we recommend for Mid-South commercial properties.",
    date: "2026-04-10",
    author: "901 Lots",
    readTime: "5 min read",
    tags: ["Sealcoating", "Maintenance", "Memphis"],
    image:
      "/img/sealcoating-worker.jpg",
    blocks: [
      { type: "p", text: "If you own a parking lot in the Mid-South, you've felt it — the summer heat that softens asphalt by 2 p.m., the surprise January cold snaps that crack pavement open, and the muggy humidity that never really leaves. All of these chip away at the surface of your lot every year." },
      { type: "p", text: "Sealcoating is the single most cost-effective thing you can do to extend pavement life. The question we get every week from Memphis-area property managers is: how often is the right cadence?" },
      { type: "h2", text: "The short answer" },
      { type: "p", text: "For most Memphis-area commercial lots, plan on sealcoating every 2 to 3 years. Crack-fill annually between full seals. That's the schedule that protects your asphalt investment without overspending." },
      { type: "h2", text: "Why 2 to 3 years (not 5)" },
      { type: "p", text: "Manufacturers sometimes claim 5+ year sealer lifespans. In a dry, mild climate that might hold. In Memphis it does not. UV exposure breaks down the binder in untreated asphalt fast. Once you see the surface go from black to gray, the sealer has worn off — and your asphalt is now drying out, getting brittle, and starting to crack." },
      { type: "p", text: "By the time you can clearly see the gray, you're typically already two years past the sweet spot. Resealing on a 2–3 year cycle keeps you in the protective zone and avoids the much-bigger spend that comes when cracks turn into potholes." },
      { type: "h2", text: "How to know when YOUR lot needs it" },
      { type: "ul", items: [
        "The surface looks gray instead of dark black",
        "You can see small surface cracks (especially radiating from the edges)",
        "Faded striping",
        "Loose aggregate in the asphalt (small stones lifting up)",
        "Oil stains have soaked deep, not beaded on top",
      ]},
      { type: "h2", text: "What we recommend at 901 Lots" },
      { type: "p", text: "On a typical Memphis-area commercial property we suggest a simple 3-step program:" },
      { type: "ol", items: [
        "Year 1, year 2: Annual crack-fill walk. We hit the new cracks before water gets under the slab.",
        "Year 2 or 3: Full sealcoat + crack fill + restripe.",
        "Repeat.",
      ]},
      { type: "p", text: "This program typically extends the life of an asphalt lot from 15 years to 25+ years — and avoids a six-figure full repave." },
      { type: "h2", text: "Want a free assessment?" },
      { type: "p", text: "We'll walk your lot, give you a written maintenance plan, and quote the next step — no obligation. Tell us about your property and we'll get a date on the calendar." },
    ],
  },
  {
    slug: "ada-parking-compliance-mid-south",
    title: "ADA parking compliance basics for Mid-South business owners",
    description:
      "ADA-compliant parking is the law and a customer-service must. Here's what every Mid-South business owner needs to know — without the legal jargon.",
    date: "2026-04-22",
    author: "901 Lots",
    readTime: "6 min read",
    tags: ["ADA", "Compliance", "Striping"],
    image:
      "/img/striping-fresh.jpg",
    blocks: [
      { type: "p", text: "Every commercial parking lot in the United States is required to have accessible parking under the Americans with Disabilities Act (ADA). For Mid-South business owners, this is not just a legal compliance issue — it's a customer-service issue. Doing it right means more customers can comfortably visit your business." },
      { type: "p", text: "Below is the plain-English version of what you need to know. (This is general guidance, not legal advice — but it gets 95% of business owners aligned with the standard.)" },
      { type: "h2", text: "How many accessible stalls do I need?" },
      { type: "p", text: "Here's the rough rule:" },
      { type: "ul", items: [
        "1–25 total stalls → at least 1 accessible stall",
        "26–50 stalls → at least 2",
        "51–75 stalls → at least 3",
        "76–100 stalls → at least 4",
        "101–150 stalls → at least 5",
        "Every 100 stalls after that → 1 more accessible stall",
      ]},
      { type: "p", text: "At least 1 in every 6 accessible stalls must be van-accessible (with the wider access aisle)." },
      { type: "h2", text: "Stall and aisle dimensions" },
      { type: "ul", items: [
        "Standard accessible stall: 96 inches wide minimum",
        "Van-accessible stall: 96 inches wide PLUS a 96-inch-wide access aisle",
        "Standard accessible aisle (next to a car stall): 60 inches wide",
        "Two adjacent accessible stalls can share one access aisle",
      ]},
      { type: "h2", text: "Signage" },
      { type: "p", text: "Every accessible stall needs a vertical sign with the International Symbol of Accessibility, mounted high enough that it stays visible when a vehicle is parked in the stall (typically 60 inches from the ground to the bottom of the sign). Van-accessible stalls need a 'Van Accessible' sign as well." },
      { type: "h2", text: "Surface and slope" },
      { type: "p", text: "Accessible stalls and aisles must be on a level surface — slope cannot exceed 1:48 in any direction. This is one of the most common compliance failures we find on older lots that have settled over time." },
      { type: "h2", text: "Common mistakes we fix" },
      { type: "ul", items: [
        "Faded blue paint and missing access-aisle striping",
        "Signs that have fallen, are too low, or are missing the 'Van Accessible' tag",
        "Accessible stalls placed on a noticeable slope",
        "Too few accessible stalls for the total lot count",
        "Striping that doesn't match the current International Symbol of Access",
      ]},
      { type: "h2", text: "Need an ADA audit?" },
      { type: "p", text: "We provide free ADA compliance walk-throughs for Mid-South business owners. We'll mark up your lot, identify gaps, and quote the fix — usually finished in a single overnight visit." },
    ],
  },
  {
    slug: "striping-vs-restriping",
    title: "Striping vs. restriping: which does your parking lot need?",
    description:
      "When can you simply re-paint over existing lines, and when do you need a full new layout? Here's how we decide on every Mid-South project.",
    date: "2026-05-04",
    author: "901 Lots",
    readTime: "4 min read",
    tags: ["Striping", "Restriping", "Maintenance"],
    image:
      "/img/shopping-center.jpg",
    blocks: [
      { type: "p", text: "Customers ask us this all the time: 'My lines are faded — do I need fresh striping or a full restriping?' The honest answer is that the words mean roughly the same thing in our industry. What actually matters is whether you need a new layout or a same-layout repaint. Here's how to tell." },
      { type: "h2", text: "Re-paint over existing layout (the cheap, fast option)" },
      { type: "p", text: "If your current layout works — customers know where to park, traffic flows well, you have the right number of stalls — and the lines are simply faded, you just need a repaint. We mask, paint, and move on. Typical timeline: a single overnight visit. Typical cost: a fraction of new layout work." },
      { type: "h2", text: "When you actually need a new layout" },
      { type: "ul", items: [
        "You recently resurfaced or sealcoated — there's nothing to paint over",
        "Your stall count or use case has changed (new tenant, drive-thru added, EV charging stalls needed)",
        "Customers regularly park crooked or in the wrong spots (the current layout is confusing)",
        "ADA stalls aren't compliant with current standards",
        "You're rebranding — new directional signage, colors, or custom markings",
      ]},
      { type: "h2", text: "What we do in either case" },
      { type: "ol", items: [
        "Walk the lot with you and confirm the goal",
        "Mask carefully so paint doesn't bleed onto adjacent surfaces",
        "Apply commercial-grade traffic paint at the right thickness",
        "Touch up any signage and curb painting",
        "Final walk-through with you before we leave",
      ]},
      { type: "h2", text: "Same crew, same standard, smaller bill" },
      { type: "p", text: "Whether your project is a quick same-layout repaint or a full new layout for a 200-stall retail center, the standard is the same. The price isn't. Tell us what you need and we'll quote both options so you can make the call." },
    ],
  },
];

export const POSTS_BY_SLUG = Object.fromEntries(POSTS.map((p) => [p.slug, p]));
